import { goKeys as k } from "@/i18n/go-keys"
import { blobMediaUrl } from "@/lib/blob-url"

/* ── Laura Philipp campaign photography (IRONMAN Hamburg 2026 shoot, rights
   cleared for the ambassador surfaces). Blob-hosted per /media-handling. ── */
export const PHOTOS = {
  /** Hero — on the bike in the aero position, subject right of centre so the
   *  copy column sits on the open sky to her left. */
  bike: blobMediaUrl("/images/marketing/laura-ambassador-bike.jpg"),
  /** Ambassador quote band background — running, subject in the left half. */
  run: blobMediaUrl("/images/marketing/laura-ambassador-run.jpg"),
  /** Reveal block — running a mountain trail above St. Moritz. Portrait crop,
   *  so the subject sits right of centre in the source frame. */
  mountainRun: blobMediaUrl("/images/marketing/laura-mountain-run.jpg"),
  /**
   * Closing band — breaking the tape at IRONMAN 70.3 Zell am See.
   *
   * Portrait source (3299x4948) in a full-bleed landscape band, so the band
   * shows a horizontal slice of it. That is the reason to use this frame: the
   * slice is centred on the Ahead logo on her suit, which no landscape crop of
   * the previous frame — her mid-chute at IRONMAN Kalmar, small in a wide
   * shot — could foreground.
   *
   * It also needs no anonymising treatment, unlike the frame it
   * replaces. The crowd either side of her is far outside the plane of focus —
   * checked on the 3299px original rather than a downscale, on both flanks at
   * the height the band actually shows — so no face is resolvable and there is
   * nothing to blur.
   *
   * Smaller than the other frames (3299 wide against 3840), which is the one
   * trade: on a 2x display wider than ~1650 CSS px the optimiser has no more
   * pixels to give.
   */
  zellamSeeFinish: blobMediaUrl("/images/marketing/laura-finish-zellamsee.jpg"),
  /**
   * Quote-card avatar — face crop from the Zell am See 70.3 finish. Replaces
   * the podium frame, which showed the whole podium and left Laura
   * unrecognisable at 60px, alongside two other identifiable athletes.
   */
  portrait: blobMediaUrl("/images/marketing/laura-portrait-zellamsee.jpg"),
} as const

/**
 * Where Laura actually is inside each full-bleed frame, and how big the source
 * is — normalised, origin top-left.
 *
 * These exist because the framing of a full-bleed photo is not checkable by
 * eye at every window size, and getting it wrong is invisible at the widths we
 * develop at. Both bands cropped her head off above 2560 while looking perfect
 * at 1440. `e2e/lauraphilipps-responsive.spec.ts` reads these and asserts she
 * is still in frame across the range.
 *
 * `topmost` is optional: set it where something above the head must stay in
 * frame (raised arms, a held tape). The guard asserts real headroom for it,
 * not mere presence — `head` being inside the band was true the whole time her
 * hands were being sliced off.
 *
 * If you swap a photo, re-measure and update the entry. A stale entry makes
 * the test assert the wrong thing, which is worse than no test.
 */
export const PHOTO_SUBJECTS = {
  /** Quote band. Head high and left; she runs away from camera. */
  run: { width: 3840, height: 2557, head: { x: 0.245, y: 0.2 } },
  /**
   * Closing band. Portrait frame; she fills it, head high, logo mid-chest.
   * `topmost` is her raised hands on the finish tape — the highest thing that
   * has to survive the crop, and the thing that was being cut while `head`
   * alone still passed the guard.
   *
   * `faceLeft` is the left edge of her visor, measured off the rendered band.
   * Two things at `lg` have to stay clear of it: the copy column, and the end
   * of the blur mask's ramp. Her raised arms reach further left than this and
   * the copy does overlap them at the widest widths — deliberately; the face
   * is the line that matters, and it is the one the guard holds.
   */
  zellamSeeFinish: {
    width: 3299,
    height: 4948,
    head: { x: 0.5, y: 0.32 },
    topmost: { x: 0.5, y: 0.228 },
    faceLeft: 0.413,
  },
} as const

/* ── The practical part — /dev/home-v3's four steps, pictures included. The
   first three pictures are that page's process drafts, copied to their own
   Blob prefix so later work on the homepage drafts can't change this page
   under it; the plan and the follow-up are the membership page's. The drafts
   are 3:2; the plan (8:7) and the follow-up (615x540) give up a strip top and
   bottom.

   Each market sees four: the scan step where the scan sells, the follow-up
   test where it doesn't. The follow-up closes the row because it only makes
   sense after the plan — and it promises a second booking, not an included
   retest: membership rides the same flag as the scan, so a blood-only market
   has no follow-ups to include. ── */
export const STEPS = [
  {
    titleKey: k.athleteStep1Title,
    bodyKey: k.athleteStep1Body,
    altKey: k.athleteStep1ImageAlt,
    image: blobMediaUrl("/images/marketing/lauraphilipps-steps/01-book.webp"),
  },
  {
    titleKey: k.athleteStep2Title,
    bodyKey: k.athleteStep2Body,
    altKey: k.athleteStep2ImageAlt,
    image: blobMediaUrl("/images/marketing/lauraphilipps-steps/02-blood.webp"),
  },
  {
    titleKey: k.athleteStep3Title,
    bodyKey: k.athleteStep3Body,
    altKey: k.athleteStep3ImageAlt,
    image: blobMediaUrl("/images/marketing/lauraphilipps-steps/03-scan.png"),
    // Only where the scan is sellable; a blood-only market gets three steps.
    imaging: true,
  },
  {
    titleKey: k.athleteStep4Title,
    bodyKey: k.athleteStep4Body,
    altKey: k.athleteStep4ImageAlt,
    image: blobMediaUrl("/images/membership/included-action-plan.jpg"),
  },
  {
    titleKey: k.athleteStepFollowUpTitle,
    bodyKey: k.athleteStepFollowUpBody,
    altKey: k.athleteStepFollowUpImageAlt,
    image: blobMediaUrl("/images/membership/included-cadence.jpg"),
    bloodOnly: true,
  },
] as const

/** The four "fit does not mean healthy" rows in the dark reveal band. */
export const REVEAL_ROWS = [
  k.athleteRevealRow1,
  k.athleteRevealRow2,
  k.athleteRevealRow3,
  k.athleteRevealRow4,
] as const
